import {useMemo} from 'react';
import {create} from 'zustand';

import {
  POINT_VISUALIZATION_OPTIONS,
  PointVisualizationOptions,
} from '../../components/WorkspaceSettingsModal/controls/PointVisualization';
import {TooltipNumberOfRunsOptions} from '../../util/panelsettings';
import {OrganizationPrefix} from './../../components/PanelBank/types';
import {useViewer} from './../../state/viewer/hooks';
import {DEFAULT_MAX_NUM_RUNS, DEFAULT_NUM_RUNS_NEW} from './defaults';

// defaults are set from https://wandb.atlassian.net/browse/WB-17958
export const DefaultWorkspaceSettings = {
  pointVisualizationMethod: POINT_VISUALIZATION_OPTIONS.SamplingByDefault,

  // sections
  showEmptySections: false,
  autoOrganizePrefix: OrganizationPrefix.LastPrefix,
  sortAlphabetically: false,

  // panels
  suppressLegends: false,

  // tooltips
  tooltipNumberOfRuns: TooltipNumberOfRunsOptions.Default,
  highlightedCompanionRunOnly: false,
  colorRunNames: true,

  // runs
};

export type WorkspaceSettings = {
  /**
   * For a period of time users were able to set a max runs values across all workspaces through a single value set as a global custom default. This value is going to hang around in some user data and we need a way to use it as the default in workspaces when the feature flag is flipped. This lets us read that value in (if it exists) and use it in the logic below
   */
  deprecatedMaxRuns: number | null;

  // feature
  /**
   * @deprecated
   * Don't read this value from the store, read it from `usePointVisualizationMethod` because there is specific defaulting that is user / org aware and plumbing that through the store is way more complicated than letting the store run simply and conditionally overwriting a value READ from the store when other conditions require special handling
   */
  pointVisualizationMethod: PointVisualizationOptions; // controls if a user is bucketing w/ weave or using legacy sampling

  // sections
  showEmptySections: boolean;
  autoOrganizePrefix: OrganizationPrefix;
  sortAlphabetically: boolean;

  // panels
  suppressLegends: boolean; // controls whether to show legends in runs line plots

  // tooltips

  tooltipNumberOfRuns: TooltipNumberOfRunsOptions; // controls how many runs show in the tooltips
  highlightedCompanionRunOnly: boolean; // controls showing only one run on companion plots
  colorRunNames: boolean; // controls whether run names inherit colors

  // runs
  maxRuns?: number; // controls number of runs shown per runs line plot
};

export type EditableWorkspaceSettings = Omit<
  WorkspaceSettings,
  'deprecatedMaxRuns'
>;

export type WorkspaceSettingsUpdaters = {
  // whenever a workspace unmounts we need to clear it so it doesn't hang around for a new workspace. The new workspace will mount its own value
  reset: () => void;
  // zustand merges by default so we can use a single updater
  update: (state: Partial<WorkspaceSettings>) => void;
};

export const WORKSPACE_SETTING_SOURCE: Record<
  keyof EditableWorkspaceSettings,
  'panelBankSettings' | 'panelSettings'
> = {
  autoOrganizePrefix: 'panelBankSettings',
  colorRunNames: 'panelSettings',
  highlightedCompanionRunOnly: 'panelBankSettings',
  maxRuns: 'panelSettings',
  pointVisualizationMethod: 'panelSettings',
  showEmptySections: 'panelBankSettings',
  sortAlphabetically: 'panelBankSettings',
  suppressLegends: 'panelSettings',
  tooltipNumberOfRuns: 'panelSettings',
};

export const getWorkspaceSettings = create<
  WorkspaceSettings & WorkspaceSettingsUpdaters
>()(set => ({
  // feature defaults
  deprecatedMaxRuns: null,
  pointVisualizationMethod: DefaultWorkspaceSettings.pointVisualizationMethod,

  // handlers
  reset: () =>
    set(() => ({
      colorRunNames: DefaultWorkspaceSettings.colorRunNames,
      highlightedCompanionRunOnly:
        DefaultWorkspaceSettings.highlightedCompanionRunOnly,
      tooltipNumberOfRuns: DefaultWorkspaceSettings.tooltipNumberOfRuns,
    })),
  update: (newState: Partial<WorkspaceSettings>) => {
    set(() => newState);
  },

  // sections
  showEmptySections: DefaultWorkspaceSettings.showEmptySections,
  autoOrganizePrefix: DefaultWorkspaceSettings.autoOrganizePrefix,
  sortAlphabetically: DefaultWorkspaceSettings.sortAlphabetically,

  // panels
  suppressLegends: DefaultWorkspaceSettings.suppressLegends,

  // tooltips
  colorRunNames: DefaultWorkspaceSettings.colorRunNames,
  highlightedCompanionRunOnly:
    DefaultWorkspaceSettings.highlightedCompanionRunOnly,

  // runs
  tooltipNumberOfRuns: DefaultWorkspaceSettings.tooltipNumberOfRuns,
}));

export const useWorkspaceSettings = (): Required<EditableWorkspaceSettings> => {
  const maxRuns = useMaxRuns();
  const settings = getWorkspaceSettings(s => s);

  return useMemo(
    () => ({
      showEmptySections: settings.showEmptySections,
      autoOrganizePrefix: settings.autoOrganizePrefix,
      sortAlphabetically: settings.sortAlphabetically,

      suppressLegends: settings.suppressLegends,
      colorRunNames: settings.colorRunNames,
      highlightedCompanionRunOnly: settings.highlightedCompanionRunOnly,

      tooltipNumberOfRuns: settings.tooltipNumberOfRuns,

      maxRuns,
      pointVisualizationMethod: settings.pointVisualizationMethod,
    }),
    [settings, maxRuns]
  );
};

// This hook keeps a user's workspace max run setting from leaking if the feature flag is turned off
export const useMaxRuns = (
  config: {
    getSettings: typeof getWorkspaceSettings;
    getViewer: typeof useViewer;
  } = {
    getSettings: getWorkspaceSettings,
    getViewer: useViewer,
  }
) => {
  const viewer = config.getViewer();
  const {deprecatedMaxRuns, maxRuns, update} = config.getSettings(state => ({
    deprecatedMaxRuns: state.deprecatedMaxRuns,
    maxRuns: state.maxRuns,
    update: state.update,
  }));

  // @ts-ignore - custom max runs is deprecated so it's not on the type
  const legacyRuns = viewer?.userInfo?.betaFeatures?.['custom-max-runs'];

  if (typeof legacyRuns === 'number' && legacyRuns !== deprecatedMaxRuns) {
    update({deprecatedMaxRuns: legacyRuns});
  }

  /**
   * 1. If a user has a max runs setting on the workspace use that
   * 2. If a user has a deprecated max runs setting from custom defaults use that
   * 3. Otherwise return the default
   */

  /**
   * Make sure a user can't sneak in values higher than the max
   */
  if (maxRuns) {
    return maxRuns > DEFAULT_MAX_NUM_RUNS ? DEFAULT_MAX_NUM_RUNS : maxRuns;
  }

  if (deprecatedMaxRuns) {
    return deprecatedMaxRuns > DEFAULT_MAX_NUM_RUNS
      ? DEFAULT_MAX_NUM_RUNS
      : deprecatedMaxRuns;
  }

  return DEFAULT_NUM_RUNS_NEW;
};
